import { c as _c } from "react/compiler-runtime";
import type { CellContext, InitialTableState, RowData } from '@tanstack/react-table';
import usePagination from '@mui/material/usePagination';
import NumberFlow from '@number-flow/react';
import { createColumnHelper, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { useImmerAtom } from 'jotai-immer';
import { Fragment, useRef } from 'react';
import type { Subtitles } from '@/app/[locale]/subtitles/_api/os';
import type { SubtitleData } from '@/app/[locale]/subtitles/_components/columns';
import type { RowSelectionChangeFn } from '@/types/utils';
import { openSubtitlesSubtitlesAtom } from '@/app/[locale]/subtitles/_api/os';
import { buildInitialTableState, initialTableStateFamily, mediaSubtitleFamily, osLanguageAtom } from '@/app/[locale]/subtitles/_atoms';
import { useCommonColumns } from '@/app/[locale]/subtitles/_components/columns';
import { RefetchButton } from '@/app/[locale]/subtitles/_components/menu-items';
import { getFileId } from '@/app/[locale]/subtitles/_utils';
import { SortIcon } from '@/components/my-icon/sort-icon';
import { TableGoToLastPage } from '@/components/my-table/go-to-last-page';
import { TablePagination } from '@/components/my-table/pagination';
import { TablePaginationSizeSelect } from '@/components/my-table/pagination-size-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Div } from '@/components/ui/html-elements';
import { OverflowTooltipText } from '@/components/ui/overflow-tooltip-text';
import { Separator } from '@/components/ui/separator';
import { HeaderTitle, TableDataCell, TableHeader, TableHeaderCell, TableHeaderCellRender, TableRow } from '@/components/ui/table-element';
import { filterFn, sortBySelection } from '@/lib/table-utils';
import { useI18n } from '@/locales/client';
import { useClone } from '@sub-vocab/utils/hooks';
import { findClosest } from '@sub-vocab/utils/lib';
type MovieSubtitleData = SubtitleData;
function useMovieColumns(rootRef) {
  const $ = _c(10);
  useI18n();
  let t0;
  let t1;
  if ($[0] !== rootRef) {
    const columnHelper = createColumnHelper();
    let t2;
    if ($[3] !== rootRef?.current) {
      t2 = t3 => {
        const {
          header
        } = t3;
        const isSorted = header.column.getIsSorted();
        return <TableHeaderCell header={header} className="w-[.1%] [&:active:has(.child:not(:active))]:signal/active [&:active:has(.child:not(:active))+th]:signal/active"><Div className="justify-between gap-1 pr-1 pl-2 select-none signal/active:bg-background-active" onClick={e => {
            header.column.getToggleSortingHandler()?.(e);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><div className="child flex" /><SortIcon isSorted={isSorted} /></Div></TableHeaderCell>;
      };
      $[3] = rootRef?.current;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t0 = columnHelper.accessor(_temp, {
      id: "action",
      sortingFn: sortBySelection,
      header: t2,
      cell: _temp3
    });
    let t3;
    if ($[5] !== rootRef?.current) {
      t3 = t4 => {
        const {
          header: header_0
        } = t4;
        const isSorted_0 = header_0.column.getIsSorted();
        return <TableHeaderCell header={header_0} className="active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1" onClick={e_1 => {
            header_0.column.getToggleSortingHandler()?.(e_1);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Name" isSorted={isSorted_0} className="*:data-title:text-left" /></Div></TableHeaderCell>;
      };
      $[5] = rootRef?.current;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t1 = columnHelper.accessor(_temp4, {
      id: "movie_name",
      filterFn,
      header: t3,
      cell: ctx => <MovieNameCell {...ctx} rootRef={rootRef} />
    });
    $[0] = rootRef;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = [t0, t1];
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function _temp4(row_1) {
  return row_1.subtitle.attributes.files[0]?.file_name || "";
}
function _temp3(t0) {
  const {
    row: row_0,
    cell,
    onRowSelectionChange
  } = t0;
  const canExpand = row_0.getCanExpand();
  return <TableDataCell cell={cell}><Div className="text-zinc-400"><div className={clsx("flex h-full grow items-center justify-between pr-1 pl-2", canExpand && "cursor-pointer")}><Checkbox className={clsx("rounded-full sq:rounded-full sq:[corner-shape:round]!")} onClick={_temp2} checked={row_0.getIsSelected()} onCheckedChange={checked => {
          onRowSelectionChange?.(checked, row_0, "singleRow");
        }} />{"\u200B"}</div></Div></TableDataCell>;
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(row) {
  return row.subtitle.id;
}
function MovieNameCell(t0) {
  const $ = _c(8);
  const {
    rootRef,
    cell,
    getValue
  } = t0;
  let t1;
  if ($[0] !== getValue) {
    t1 = getValue();
    $[0] = getValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const value = t1;
  let t2;
  if ($[2] !== rootRef || $[3] !== value) {
    t2 = <Div className="cursor-text py-1 pr-px pl-2.5" onClick={_temp5}><OverflowTooltipText rootRef={rootRef} value={value} className="text-sm tracking-[.04em]" /></Div>;
    $[2] = rootRef;
    $[3] = value;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== cell || $[6] !== t2) {
    t3 = <TableDataCell cell={cell}>{t2}</TableDataCell>;
    $[5] = cell;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/// keep-unique
function _temp5(ev) {
  return ev.stopPropagation();
}
const PAGE_SIZES = [4, 5, 10, 20, 40, 50, 100, 200] as const;
export function MovieSubtitleFiles(t0) {
  const $ = _c(14);
  const {
    id
  } = t0;
  const [language] = useAtom(osLanguageAtom);
  let t1;
  if ($[0] !== id || $[1] !== language) {
    t1 = openSubtitlesSubtitlesAtom({
      type: "movie",
      tmdb_id: id,
      languages: language,
      per_page: 100
    });
    $[0] = id;
    $[1] = language;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isFetching,
    refetch
  } = useAtomValue(t1);
  let t2;
  if ($[3] !== isFetching || $[4] !== refetch) {
    t2 = <div><div className="flex h-9 gap-2 p-1.5"><RefetchButton refetch={refetch} isFetching={isFetching} /></div></div>;
    $[3] = isFetching;
    $[4] = refetch;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== data) {
    t3 = data ?? [];
    $[6] = data;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== id || $[9] !== t3) {
    t4 = <SubtitleFiles id={id} subtitleData={t3} />;
    $[8] = id;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t4) {
    t5 = <Fragment>{t2}{t4}</Fragment>;
    $[11] = t2;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function SubtitleFiles(t0) {
  const $ = _c(60);
  const {
    id,
    subtitleData
  } = t0;
  const t = useI18n();
  const store = useStore();
  const rootRef = useRef(null);
  const commonColumns = useCommonColumns(rootRef);
  const movieColumns = useMovieColumns(rootRef);
  const columns = [...commonColumns, ...movieColumns];
  let t1;
  if ($[0] !== id) {
    t1 = [id];
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2, setMediaSubtitleState] = useImmerAtom(mediaSubtitleFamily.useA(t1));
  const {
    tableState: mediaTableState
  } = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = buildInitialTableState({
      sorting: [{
        id: "download_count",
        desc: true
      }],
      columnOrder: ["action", "movie_name", "language", "upload_date", "download_count"],
      pagination: {
        pageSize: findClosest(10, PAGE_SIZES)
      }
    } satisfies InitialTableState);
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== id) {
    t4 = [id, t3];
    $[3] = id;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const mediaSubtitleInitialTableStateAtom = initialTableStateFamily.useA(t4);
  const table = useClone(useReactTable({
    data: subtitleData.map(_temp6),
    columns,
    initialState: store.get(mediaSubtitleInitialTableStateAtom),
    state: mediaTableState,
    autoResetPageIndex: false,
    getRowId: getFileId,
    getRowCanExpand: _temp7,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onStateChange: updater => {
      store.set(mediaSubtitleInitialTableStateAtom, typeof updater === "function" ? updater(tableState) : updater);
    }
  }));
  let t5;
  if ($[5] !== table) {
    t5 = table.getFilteredRowModel();
    $[5] = table;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const rowsFiltered = t5.rows;
  const tableState = table.getState();
  let t6;
  if ($[7] !== table) {
    t6 = table.getPageCount();
    $[7] = table;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = tableState.pagination.pageIndex + 1;
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = {
      count: t6,
      page: t7
    };
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    items
  } = usePagination(t8);
  let t9;
  if ($[12] !== setMediaSubtitleState) {
    t9 = (checked, row, mode) => {
      setMediaSubtitleState(t10 => {
        const {
          tableState: tableState_0
        } = t10;
        if (mode === "singleRow") {
          tableState_0.rowSelection = {};
        } else {
          if (mode === "singleSubRow") {
            const subRows = row.getParentRow()?.subRows ?? [];
            subRows.forEach(t11 => {
              const {
                id: id_0
              } = t11;
              tableState_0.rowSelection[id_0] = false;
            });
          }
        }
        tableState_0.rowSelection[row.id] = Boolean(checked);
      });
    };
    $[12] = setMediaSubtitleState;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleRowSelectionChange = t9;
  let t10;
  if ($[14] !== table) {
    t10 = table.getHeaderGroups();
    $[14] = table;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = useClone(t10);
  let t12;
  if ($[16] !== t11) {
    t12 = t11.map(_temp9);
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12) {
    t13 = <TableHeader>{t12}</TableHeader>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== table) {
    t14 = table.getRowModel();
    $[20] = table;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = useClone(t14.rows);
  let t16;
  if ($[22] !== handleRowSelectionChange || $[23] !== t15) {
    let t17;
    if ($[25] !== handleRowSelectionChange) {
      t17 = row_0 => <TableRow key={row_0.id} row={row_0} onRowSelectionChange={handleRowSelectionChange} />;
      $[25] = handleRowSelectionChange;
      $[26] = t17;
    } else {
      t17 = $[26];
    }
    t16 = t15.map(t17);
    $[22] = handleRowSelectionChange;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[27] !== t16) {
    t17 = <tbody>{t16}</tbody>;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t13 || $[30] !== t17) {
    t18 = <table className="relative border-separate border-spacing-0">{t13}{t17}</table>;
    $[29] = t13;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== table) {
    t19 = <TableGoToLastPage table={table} />;
    $[32] = table;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t18 || $[35] !== t19) {
    t20 = <div ref={rootRef} className="grow overflow-auto overflow-y-scroll">{t18}{t19}</div>;
    $[34] = t18;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== items || $[38] !== table) {
    t21 = <TablePagination items={items} table={table} rootRef={rootRef} />;
    $[37] = items;
    $[38] = table;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== table || $[41] !== tableState.pagination.pageSize) {
    t22 = <TablePaginationSizeSelect table={table} sizes={PAGE_SIZES} value={tableState.pagination.pageSize} />;
    $[40] = table;
    $[41] = tableState.pagination.pageSize;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = `/${t("page")}`;
  let t24;
  if ($[43] !== t23) {
    t24 = <div className="px-1 whitespace-nowrap">{t23}</div>;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== t22 || $[46] !== t24) {
    t25 = <div className="flex grow items-center justify-end"><div className="flex items-center text-xs">{t22}{t24}</div></div>;
    $[45] = t22;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[48] !== t21 || $[49] !== t25) {
    t26 = <div className="flex flex-wrap items-center justify-between gap-0.5 border-t border-t-zinc-200 py-1 pr-0.5 tracking-[.03em] tabular-nums dark:border-neutral-800">{t21}{t25}</div>;
    $[48] = t21;
    $[49] = t25;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== rowsFiltered.length) {
    t27 = <NumberFlow value={rowsFiltered.length} locales="en-US" animated={true} isolate={true} />;
    $[51] = rowsFiltered.length;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span>{" subtitles"}</span>;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t27) {
    t29 = <div className="flex justify-center border-t border-solid border-t-zinc-200 bg-background dark:border-neutral-800"><div className="flex h-7 items-center text-xs tracking-[.03em] tabular-nums"><span>{t27}{t28}</span></div></div>;
    $[54] = t27;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== t20 || $[57] !== t26 || $[58] !== t29) {
    t30 = <Fragment><Fragment><Fragment>{t20}{t26}{t29}</Fragment></Fragment></Fragment>;
    $[56] = t20;
    $[57] = t26;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  return t30;
}
function _temp9(headerGroup) {
  return <tr key={headerGroup.id}>{headerGroup.headers.map(_temp8)}</tr>;
}
function _temp8(header) {
  return <TableHeaderCellRender key={header.id} header={header} />;
}
function _temp7() {
  return false;
}
function _temp6(subtitle) {
  return {
    subtitle
  };
}